import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const CONTACT_FORM_URL = 'https://readdy.ai/api/form/d7771qornr7vi0ujqkq0';

const hours = [
  { day: 'Monday', time: '9AM - 4:30PM' },
  { day: 'Tuesday', time: '9AM - 4:30PM' },
  { day: 'Wednesday', time: '9AM - 4:30PM' },
  { day: 'Thursday', time: '9AM - 4:30PM' },
  { day: 'Friday', time: '9AM - 4:30PM' },
  { day: 'Saturday', time: 'Closed' },
  { day: 'Sunday', time: 'Closed' },
];

export default function Contact() {
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '', insuranceType: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    document.title = 'Contact Summit Appalachia Insurance | Middlesboro KY 606-269-1190';
    const schema = {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      '@id': `${import.meta.env.VITE_SITE_URL}/contact#webpage`,
      url: `${import.meta.env.VITE_SITE_URL}/contact`,
      name: 'Contact Summit Appalachia Insurance | Middlesboro KY 606-269-1190',
      description: 'Contact Summit Appalachia Insurance in Middlesboro, KY for a free business insurance quote. Call 606-269-1190 or email John@summitappalachia.com.',
      isPartOf: { '@id': `${import.meta.env.VITE_SITE_URL}/#website` },
      mainEntity: {
        '@type': 'InsuranceAgency',
        name: 'Summit Appalachia Insurance',
        telephone: '+16062691190',
        email: 'John@summitappalachia.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1012 N 12th St',
          addressLocality: 'Middlesboro',
          addressRegion: 'KY',
          postalCode: '40965',
          addressCountry: 'US',
        },
        openingHoursSpecification: [
          {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
            opens: '09:00',
            closes: '16:30',
          },
        ],
      },
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-contact';
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-contact')?.remove(); };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message') setCharCount(value.length);
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (charCount > 500) return;
    setSubmitting(true);
    try {
      const body = new URLSearchParams(form as Record<string, string>);
      await fetch(CONTACT_FORM_URL, { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: body.toString() });
      setSubmitted(true);
    } catch { setSubmitted(true); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="font-sans bg-[#0a0a0a]">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[360px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/86c80d0d54b5074fd2307f8f4c77c2d6.jpg"
            alt="Contact" className="w-full h-full object-cover object-top opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/50 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pb-16 w-full">
          <p className="text-white/30 text-xs font-sans mb-3 uppercase tracking-widest">
            <Link to="/" className="hover:text-[#D4AF37] cursor-pointer">Home</Link> / Contact
          </p>
          <h1 className="font-serif text-5xl md:text-7xl text-white uppercase">Contact Us</h1>
        </div>
      </section>

      {/* Main */}
      <section className="py-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">

            {/* Left */}
            <div className="lg:col-span-2">
              <span className="text-[#c9a84c] text-xs font-bold uppercase tracking-[0.3em] font-sans">Get in Touch</span>
              <h2 className="font-serif text-3xl text-white mt-3 mb-4 uppercase">Have Questions or Need a Quote?</h2>
              <p className="text-white/50 font-sans text-sm leading-relaxed mb-10">
                Reach out and we&apos;ll get back to you as soon as possible. You can also call or email directly.
              </p>

              <div className="space-y-5 mb-10">
                <a href="mailto:John@summitappalachia.com" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-md group-hover:bg-[#D4AF37]/20 transition-colors">
                    <i className="ri-mail-line text-[#D4AF37] text-lg" />
                  </div>
                  <div>
                    <p className="text-white/30 text-xs uppercase tracking-wider font-sans">Email</p>
                    <p className="text-white text-sm font-sans group-hover:text-[#D4AF37] transition-colors">John@summitappalachia.com</p>
                  </div>
                </a>
                <a href="tel:6062691190" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-md group-hover:bg-[#D4AF37]/20 transition-colors">
                    <i className="ri-phone-line text-[#D4AF37] text-lg" />
                  </div>
                  <div>
                    <p className="text-white/30 text-xs uppercase tracking-wider font-sans">Phone</p>
                    <p className="text-white text-sm font-sans group-hover:text-[#D4AF37] transition-colors">606-269-1190</p>
                  </div>
                </a>
                <a href="https://maps.google.com/?q=1012+N+12th+St+Middlesboro+KY+40965" target="_blank" rel="nofollow" className="flex items-center gap-4 group cursor-pointer">
                  <div className="w-11 h-11 flex items-center justify-center bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-md group-hover:bg-[#D4AF37]/20 transition-colors">
                    <i className="ri-map-pin-line text-[#D4AF37] text-lg" />
                  </div>
                  <div>
                    <p className="text-white/30 text-xs uppercase tracking-wider font-sans">Address</p>
                    <p className="text-white text-sm font-sans group-hover:text-[#D4AF37] transition-colors">1012 N 12th St, Middlesboro, KY 40965</p>
                  </div>
                </a>
              </div>

              {/* Hours */}
              <div className="bg-[#111111] border border-[#D4AF37]/15 rounded-md p-6 mb-6">
                <h4 className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-5 font-sans">Business Hours of Operation</h4>
                <ul className="space-y-2">
                  {hours.map(h => (
                    <li key={h.day} className="flex justify-between text-sm font-sans">
                      <span className="text-white/40">{h.day}</span>
                      <span className={h.time === 'Closed' ? 'text-white/20' : 'text-white/70 font-semibold'}>{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Map */}
              <div className="rounded-md overflow-hidden border border-[#D4AF37]/15 w-full h-56">
                <iframe
                  title="Summit Appalachia Insurance Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3178.0!2d-83.7185!3d36.6148!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x885b3e2b2b2b2b2b%3A0x0!2s1012+N+12th+St%2C+Middlesboro%2C+KY+40965!5e0!3m2!1sen!2sus!4v1680000000000!5m2!1sen!2sus"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'invert(90%) hue-rotate(180deg)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Right Form */}
            <div className="lg:col-span-3 bg-[#111111] border border-[#D4AF37]/15 rounded-md p-8 md:p-10">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-16 text-center">
                  <div className="w-16 h-16 flex items-center justify-center bg-[#D4AF37]/10 rounded-full mb-5">
                    <i className="ri-checkbox-circle-line text-[#D4AF37] text-4xl" />
                  </div>
                  <h3 className="font-serif text-2xl text-white uppercase mb-3">Message Sent!</h3>
                  <p className="text-white/50 font-sans text-sm">Thank you for reaching out. We&apos;ll be in touch shortly.</p>
                </div>
              ) : (
                <>
                  <h3 className="font-serif text-2xl text-[#D4AF37] uppercase mb-6">Send Us a Message</h3>
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-white/30 uppercase tracking-wider mb-2 font-sans">First Name *</label>
                        <input name="firstName" value={form.firstName} onChange={handleChange} required placeholder="John"
                          className="w-full bg-[#1a1a1a] border border-[#D4AF37]/15 rounded-md px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#D4AF37] font-sans" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-white/30 uppercase tracking-wider mb-2 font-sans">Last Name *</label>
                        <input name="lastName" value={form.lastName} onChange={handleChange} required placeholder="Smith"
                          className="w-full bg-[#1a1a1a] border border-[#D4AF37]/15 rounded-md px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#D4AF37] font-sans" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-white/30 uppercase tracking-wider mb-2 font-sans">Email Address *</label>
                      <input name="email" type="email" value={form.email} onChange={handleChange} required placeholder="john@email.com"
                        className="w-full bg-[#1a1a1a] border border-[#D4AF37]/15 rounded-md px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#D4AF37] font-sans" />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold text-white/30 uppercase tracking-wider mb-2 font-sans">Phone</label>
                        <input name="phone" value={form.phone} onChange={handleChange} placeholder="606-000-0000"
                          className="w-full bg-[#1a1a1a] border border-[#D4AF37]/15 rounded-md px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#D4AF37] font-sans" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-white/30 uppercase tracking-wider mb-2 font-sans">Insurance Type</label>
                        <select name="insuranceType" value={form.insuranceType} onChange={handleChange}
                          className="w-full bg-[#1a1a1a] border border-[#D4AF37]/15 rounded-md px-4 py-3 text-sm text-white/70 focus:outline-none focus:border-[#D4AF37] font-sans cursor-pointer">
                          <option value="">Select...</option>
                          <option>General Liability</option>
                          <option>Workers Compensation</option>
                          <option>Commercial Auto</option>
                          <option>Business Owner Policy (BOP)</option>
                          <option>Specialized / High-Risk</option>
                          <option>Personal Lines</option>
                          <option>Claims Inquiry</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-white/30 uppercase tracking-wider mb-2 font-sans">Message *</label>
                      <textarea name="message" value={form.message} onChange={handleChange} required rows={5} maxLength={500}
                        placeholder="Tell us how we can help..."
                        className="w-full bg-[#1a1a1a] border border-[#D4AF37]/15 rounded-md px-4 py-3 text-sm text-white placeholder-white/20 focus:outline-none focus:border-[#D4AF37] font-sans resize-none" />
                      <p className={`text-xs mt-1 font-sans text-right ${charCount > 500 ? 'text-red-500' : 'text-white/20'}`}>{charCount}/500</p>
                    </div>
                    <div className="flex justify-end">
                      <button type="submit" disabled={submitting || charCount > 500}
                        className="px-8 py-3.5 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-widest rounded-md hover:bg-[#e8c84e] transition-colors disabled:opacity-50 whitespace-nowrap cursor-pointer font-sans">
                        {submitting ? 'Sending...' : 'Send Message'}
                      </button>
                    </div>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
